import moment from 'moment';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';

import { ChevronRightIcon, XIcon } from '@heroicons/react/solid';

import Media from './Media';
import LinkTo from '../LinkTo';
import { Button, buttonVariants } from './UI/Button';

import { usePopup } from '@/hooks/popups/use-popup';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/context/AlertProvider';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from './Forms/Input';
import TextArea from './Forms/TextArea';
import Checkbox from './Forms/Checkbox';
import { useCountries } from '@/hooks/use-countries';
import Select from './Forms/Select';
import Radio from './Forms/Radio';
import { serialize } from 'object-to-formdata';
import axios from 'axios';

const PopupAreas = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const { data: popup, isLoading } = usePopup();
  const [cookies, setCookies] = useCookies(['uyn_popup']);
  const { t } = useTranslation();

  const { data: countries = {} } = useCountries();
  const alert = useAlert();

  function closePopup() {
    setIsOpen(false);
  }

  function closePopupConfirm() {
    setIsOpenConfirm(false);
  }

  const formik = useFormik({
    validateOnChange: false,
    initialValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      company_name: '',
      country: '',
      experience: 'false',
      arrival_date: '',
      arrival_special_needs: '',
      departure_date: '',
      departure_special_needs: '',
      food_remarks_other: '',
      privacy: false
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t('REQUIRED_FIELD')),
      surname: Yup.string().required(t('REQUIRED_FIELD')),
      phone: Yup.string()
        .matches(/^\+?[0-9]+$/, t('INVALID_PHONE_NUMBER'))
        .required(t('REQUIRED_FIELD')),
      company_name: Yup.string().required(t('REQUIRED_FIELD')),
      country: Yup.string().required(t('REQUIRED_FIELD')),
      arrival_date: Yup.string()
        .required(t('REQUIRED_FIELD'))
        //prevent saturday and sunday
        .test('is-weekday', t('P_AREAS.FORM.WEEKEND_ERROR'), function (value) {
          return moment(value).day() !== 0 && moment(value).day() !== 6;
        }),

      departure_date: Yup.string()
        .required(t('REQUIRED_FIELD'))
        //prevent saturday and sunday
        .test('is-weekday', t('P_AREAS.FORM.WEEKEND_ERROR'), function (value) {
          return moment(value).day() !== 0 && moment(value).day() !== 6;
        }),
      email: Yup.string()
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
          t('INVALID_EMAIL')
        )
        .required(t('REQUIRED_FIELD')),
      privacy: Yup.boolean().oneOf([true]).required()
    }),
    async onSubmit(values) {
      try {
        await axios.post(
          '/api/areas',
          serialize({ ...values, formName: 'areas' })
        );
        formik.resetForm();
        setIsOpen(false);
        setTimeout(() => {
          setIsOpenConfirm(true);
        }, 100);
      } catch (error) {
        alert.error(t('GENERIC_ERROR'));
      }
    }
  });

  return (
    <>
      <div
        aria-label={t('P_AREAS.BANNER.BTN')}
        className={buttonVariants({ variant: 'light' })}
        onClick={() => setIsOpen(true)}
      >
        {t('P_AREAS.BANNER.BTN')}
      </div>
      <Dialog open={isOpen} onClose={closePopup} className="relative z-50">
        <Dialog.Backdrop className="fixed inset-0 bg-black/40" />

        <div className="fixed inset-0 flex items-center flex-col justify-center px-6  bg-black/40">
          <Dialog.Panel className="w-full max-w-4xl bg-white rounded-lg overflow-hidden  overflow-y-auto shadow-xl max-h-[90vh] h-full top-10">
            <div className="order-first flex flex-col text-black p-4 xl:p-6 md:order-last relative">
              <div className="w-full flex justify-end absolute top-6 right-6">
                <button
                  onClick={closePopup}
                  className="bg-black text-white rounded-full w-12 h-12 flex justify-center items-center focus:outline-none"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 flex flex-col justify-center space-y-6 p-3 md:p-6">
                <div className="space-y-6">
                  <div className="max-w-[80%]">
                    <div className="h3">{t('P_AREAS.FORM.TITLE')}</div>
                    <div className="text-xl">{t('P_AREAS.FORM.SUBTITLE')}</div>
                  </div>

                  <form onSubmit={formik.handleSubmit} className="space-y-10">
                    {/* STEP 1 */}
                    <div className="space-y-3 md:space-y-3">
                      <div className="flex flex-col md:flex-row md:space-x-4 md:space-y-0 space-y-3">
                        <Input
                          type="text"
                          required
                          name="name"
                          placeholder={t('NAME')}
                          className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          error={formik.errors.name}
                        />
                        <Input
                          type="text"
                          required
                          name="surname"
                          placeholder={t('SURNAME')}
                          className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                          value={formik.values.surname}
                          onChange={formik.handleChange}
                          error={formik.errors.surname}
                        />
                      </div>

                      <Input
                        type="email"
                        name="email"
                        required
                        placeholder={t('MAIL_ADDRESS')}
                        className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.errors.email}
                      />
                      <Input
                        type="phone"
                        name="phone"
                        required
                        placeholder={t('PHONE')}
                        className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        error={formik.errors.phone}
                      />

                      <Input
                        type="text"
                        required
                        name="company_name"
                        placeholder={t('COMPANY_NAME')}
                        className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                        value={formik.values.company_name}
                        onChange={formik.handleChange}
                        error={formik.errors.company_name}
                      />

                      <Select
                        name="country"
                        required
                        placeholder={t('P_AREAS.FORM.COUNTRY')}
                        className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                        value={formik.values.country}
                        onChange={formik.handleChange}
                        error={formik.errors.country}
                      >
                        <option value="">
                          {t('P_AREAS.FORM.SELECT_COUNTRY')}
                        </option>
                        {Object.entries(countries).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    {/* FINE STEP 1 */}

                    {/* STEP 2 */}
                    <div className="space-y-3 md:space-y-3">
                      <div className="h5">
                        {t('P_AREAS.FORM.EXPERIENCE_TITLE')}
                      </div>

                      <div className="flex flex-col md:flex-row md:space-x-4 md:space-y-0 space-y-3">
                        <div className="flex-1 border border-gray-400 rounded-md p-2 md:p-3 bg-transparent focus:ring-gray-400 focus:border-gray-400">
                          <Radio
                            name="experience"
                            checked={formik.values.experience === 'areas-tour'}
                            onChange={formik.handleChange}
                            error={formik.errors.experience}
                            value="areas-tour"
                          >
                            {t('P_AREAS.FORM.AREAS_TOUR')}
                          </Radio>
                        </div>
                        <div className="flex-1 border border-gray-400 rounded-md p-2 md:p-3 bg-transparent focus:ring-gray-400 focus:border-gray-400">
                          <Radio
                            name="experience"
                            checked={
                              formik.values.experience === 'areas-tour-outdoor'
                            }
                            onChange={formik.handleChange}
                            error={formik.errors.experience}
                            value="areas-tour-outdoor"
                          >
                            {t('P_AREAS.FORM.AREAS_TOUR_OUTDOOR')}
                          </Radio>
                        </div>
                      </div>
                    </div>
                    {/* FINE STEP 2 */}

                    {/* STEP 3 */}
                    <div className="">
                      <div className="flex flex-col md:flex-row md:space-x-4 md:space-y-0 space-y-3">
                        <div className="w-full space-y-2">
                          <div className="h5">
                            {t('P_AREAS.FORM.ARRIVAL_TITLE')}
                          </div>
                          <Input
                            type="date"
                            name="arrival_date"
                            placeholder={t('P_AREAS.FORM.ARRIVAL_DATE')}
                            className="bg-transparent focus:ring-gray-400 focus:border-gray-400 cursor-pointer"
                            value={formik.values.arrival_date}
                            onChange={formik.handleChange}
                            error={formik.errors.arrival_date}
                          />

                          <TextArea
                            name="arrival_special_needs"
                            placeholder={t(
                              'P_AREAS.FORM.ARRIVAL_SPECIAL_NEEDS'
                            )}
                            className="bg-transparent focus:ring-gray-400 focus:border-gray-400 mt-3"
                            value={formik.values.arrival_special_needs}
                            onChange={formik.handleChange}
                            error={formik.errors.arrival_special_needs}
                          />
                        </div>

                        <div className="w-full space-y-2">
                          <div className="h5">
                            {t('P_AREAS.FORM.DEPARTURE_TITLE')}
                          </div>
                          <Input
                            type="date"
                            name="departure_date"
                            placeholder={t('P_AREAS.FORM.DEPARTURE_DATE')}
                            className="bg-transparent focus:ring-gray-400 focus:border-gray-400 cursor-pointer"
                            value={formik.values.departure_date}
                            onChange={formik.handleChange}
                            error={formik.errors.departure_date}
                          />
                          <TextArea
                            name="departure_special_needs"
                            placeholder={t(
                              'P_AREAS.FORM.DEPARTURE_SPECIAL_NEEDS'
                            )}
                            className="bg-transparent focus:ring-gray-400 focus:border-gray-400 mt-3"
                            value={formik.values.departure_special_needs}
                            onChange={formik.handleChange}
                            error={formik.errors.departure_special_needs}
                          />
                        </div>
                      </div>
                    </div>
                    {/* FINE STEP 3 */}

                    {/* STEP 5 */}
                    <div className="space-y-3 md:space-y-3">
                      <div className="h5">
                        {t('P_AREAS.FORM.FOOD_REMARKS_TITLE')}
                      </div>

                      <div className="flex flex-col">
                        <TextArea
                          name="food_remarks_other"
                          placeholder={t('P_AREAS.FORM.FOOD_REMARKS_OTHER')}
                          className="bg-transparent focus:ring-gray-400 focus:border-gray-400 mt-3"
                          value={formik.values.food_remarks_other}
                          onChange={formik.handleChange}
                          error={formik.errors.food_remarks_other}
                        />
                      </div>
                    </div>
                    {/* FINE STEP 5 */}

                    {/* STEP 6 */}
                    <div className="space-y-3 md:space-y-3">
                      <Checkbox
                        name="privacy"
                        required
                        checked={formik.values.privacy}
                        onChange={formik.handleChange}
                        error={formik.errors.privacy}
                      >
                        {t('MENU.NEWSLETTER.PRIVACY')}
                        <a
                          href={`https://www.iubenda.com/privacy-policy/${t(
                            'IUBENDA.POLICY_ID'
                          )}`}
                          className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                          title="Privacy Policy"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('MENU.PRIVACY_POLICY')}
                        </a>
                      </Checkbox>

                      <Button
                        type="submit"
                        variant="dark"
                        className="w-full"
                        loading={formik.isSubmitting}
                      >
                        {t('SEND')}
                      </Button>
                    </div>
                    {/* FINE STEP 6 */}
                  </form>
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
      <Dialog
        open={isOpenConfirm}
        onClose={closePopupConfirm}
        className="relative z-50"
      >
        <Dialog.Backdrop className="fixed inset-0 bg-black/40" />

        <div className="fixed inset-0 flex items-center flex-col justify-center px-6  bg-black/40">
          <Dialog.Panel className="w-full max-w-4xl bg-white rounded-lg overflow-hidden  overflow-y-auto shadow-xl max-h-[90vh] h-auto top-10">
            <div className="text-black p-4 xl:p-6 relative">
              <div className="w-full flex justify-end absolute top-6 right-6">
                <button
                  onClick={closePopupConfirm}
                  className="bg-black text-white rounded-full w-12 h-12 flex justify-center items-center focus:outline-none"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 flex flex-col justify-center space-y-6 p-3 md:p-6 md:max-w-[80%] mx-auto pr-20 md:pr-6">
                <div className="space-y-6">
                  <div className="w-full">
                    <div className="h2">{t('P_AREAS.FORM.THX_TITLE')}</div>
                    <div className="text-xl">{t('P_AREAS.FORM.THX_TEXT')}</div>
                    <div className="text-xl">{t('P_AREAS.FORM.THX_TEXT2')}</div>
                  </div>
                  <div className="w-full">
                    <LinkTo
                      href="/"
                      aria-label={t('P_AREAS.FORM.THX_BTN')}
                      className={buttonVariants({ variant: 'dark' })}
                    >
                      {t('P_AREAS.FORM.THX_BTN')}
                    </LinkTo>
                  </div>
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
};

export default PopupAreas;
